import { NOTIFICATION_TYPES } from 'definitions';

/** Billing modes as a person reads them; the enum values are internal names. */
const BILLING_MODE_LABELS: Record<string, string> = {
  card: 'card payments',
  free: 'free',
  invoice: 'invoice billing',
};
const billingModeLabel = (mode: string): string => BILLING_MODE_LABELS[mode] ?? mode;

/**
 * The words for every notification type, in one server-safe place.
 *
 * This used to live only in the dashboard's descriptors.ts, which imports lucide
 * icons and so cannot be reached from the API or the crons. Email and Slack need
 * the same sentences the panel shows, and three hand-maintained copies of
 * "experiment is winning" drift within a release. Icons, tone and routing stay in
 * descriptors.ts; the strings live here.
 *
 * Derived from the definitions array rather than the Drizzle enum so this module
 * stays free of any database import - the runtime and editor bundles use helpers
 * too.
 */
export type NotificationTextType = (typeof NOTIFICATION_TYPES)[number];

// What a link needs to know. Deliberately smaller than a Notification row so the
// server can build a URL from an event it has just written.
export type NotificationSubject = {
  experimentId?: number | null;
  websiteId?: number | null;
};

export type NotificationText = {
  // Row name in the settings matrix. Not derived from the title, which is a
  // sentence about one specific experiment.
  label: string;
  title: (payload: Record<string, unknown>) => string;
  body?: (payload: Record<string, unknown>) => string;
  // Dashboard-relative, so the same value can be appended to PUBLIC_DASHBOARD_URL
  // for an email or a Slack message.
  path: (subject: NotificationSubject) => string | null;
};

const text = (payload: Record<string, unknown>, key: string): string => {
  const value = payload[key];
  return typeof value === 'string' ? value : '';
};

const PLAN_NAMES: Record<string, string> = {
  start: 'Start',
  grow: 'Grow',
  scale: 'Scale',
  business: 'Business',
  unlimited: 'Unlimited',
};
const planName = (plan: string): string => PLAN_NAMES[plan] ?? plan;

/** "Acme: " when a notice is about one of an agency's clients, so the agency knows whose plan it is. */
const clientPrefix = (payload: Record<string, unknown>): string => {
  const name = text(payload, 'accountName');
  return name ? `${name}: ` : '';
};

const count = (payload: Record<string, unknown>, key: string): string => {
  const value = payload[key];
  return typeof value === 'number' ? value.toLocaleString('en-GB') : '';
};

// Spelled out in UTC for the same reason as the trial date below: emails render on the server.
const longDate = (iso: string): string =>
  iso
    ? new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
    : '';

// improvement arrives as a percentage already (4.25 means 4.25%), which is how
// the stored procedure writes it. Scaling it again would report 425%.
const percent = (payload: Record<string, unknown>): string => {
  const value = payload.improvement;
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return `${value > 0 ? '+' : ''}${value.toFixed(2)}%`;
};

const experimentPath = (subject: NotificationSubject) =>
  subject.experimentId ? `/experiments/${subject.experimentId}` : null;

const statisticsPath = (subject: NotificationSubject) =>
  subject.experimentId ? `/experiments/${subject.experimentId}?screen=statistics` : null;

// Where the schedule is configured, which is the only place a blocked one can be
// cleared: re-saving it is what resets the state.
const settingsPath = (subject: NotificationSubject) =>
  subject.experimentId ? `/experiments/${subject.experimentId}?screen=settings` : null;

const movement = (payload: Record<string, unknown>): string => {
  const lift = percent(payload);
  const variant = text(payload, 'variantName');
  return [variant, lift && `at ${lift}`].filter(Boolean).join(' ');
};

/**
 * The facts a server key notice states: who acted and which key. Not the website: the email
 * subject and the in-app context already name it, so the body would only repeat it as a raw
 * URL. Never the secret or its hash; the payload carries only the prefix the key list also
 * shows. Each part is left out when its field is missing rather than printing an empty gap.
 */
const serverKeyFacts = (payload: Record<string, unknown>, verb: string): string => {
  const actor = text(payload, 'actorName');
  const prefix = text(payload, 'keyPrefix');
  return [actor && `${verb} by ${actor}.`, prefix && `The key starts ${prefix}.`].filter(Boolean).join(' ');
};

const serverKeyName = (payload: Record<string, unknown>): string => {
  const name = text(payload, 'keyName');
  return name ? `Server key "${name}"` : 'A server key';
};

/** A feature flag notice names the flag; its page is /flags/:id (P-PER-59). */
const flagName = (payload: Record<string, unknown>): string => {
  const name = text(payload, 'flagName');
  return name ? `"${name}"` : 'A feature flag';
};

const flagPath = ({ experimentId }: NotificationSubject) =>
  experimentId ? `/flags/${experimentId}` : '/flags/library';

export const NOTIFICATION_TEXT: Record<NotificationTextType, NotificationText> = {
  'experiment.started': {
    label: 'Experiment started',
    title: (payload) => `${text(payload, 'experimentName')} started`,
    path: experimentPath,
  },
  'experiment.ended': {
    label: 'Experiment ended',
    title: (payload) => `${text(payload, 'experimentName')} ended`,
    path: statisticsPath,
  },
  'experiment.deployed': {
    label: 'Experiment deployed',
    title: (payload) => `${text(payload, 'experimentName')} deployed`,
    body: (payload) => (text(payload, 'variantName') ? `Rolled out ${text(payload, 'variantName')}` : ''),
    path: experimentPath,
  },
  'experiment.significant.positive': {
    label: 'A variant is winning',
    title: (payload) => `${text(payload, 'experimentName')} is winning`,
    body: movement,
    path: statisticsPath,
  },
  'experiment.significant.negative': {
    label: 'A variant is losing',
    title: (payload) => `${text(payload, 'experimentName')} is losing`,
    body: movement,
    path: statisticsPath,
  },
  'experiment.srm.detected': {
    label: 'Traffic did not split as configured',
    title: (payload) => `${text(payload, 'experimentName')} did not split as configured`,
    body: () => 'Results are not trustworthy until this is explained',
    path: statisticsPath,
  },
  'experiment.no_data': {
    label: 'Experiment stopped receiving traffic',
    title: (payload) => `${text(payload, 'experimentName')} has stopped receiving traffic`,
    body: (payload) => {
      const hours = payload.windowHours;
      return typeof hours === 'number' ? `Nothing recorded in the last ${hours} hours` : 'No traffic recorded';
    },
    path: experimentPath,
  },
  'experiment.overdue': {
    label: 'Experiment past its end date',
    title: (payload) => `${text(payload, 'experimentName')} is past its end date`,
    body: () => 'Still running and still collecting traffic',
    path: statisticsPath,
  },
  /**
   * The scheduler wanted to act and would not. Worded as a thing that stopped rather
   * than a thing that failed: the engine declining to start an experiment it cannot
   * trust is it working correctly, and the reason it carries is the whole message.
   */
  'experiment.automation_blocked': {
    label: 'Automation needs a decision',
    title: (payload) => `${text(payload, 'experimentName')} needs a decision`,
    body: (payload) => text(payload, 'reason') || 'Its schedule is waiting on a person',
    path: settingsPath,
  },
  'implementation.done': {
    label: 'Experiment marked as implemented',
    title: (payload) => `${text(payload, 'experimentName')} marked as implemented`,
    path: () => '/experiments/implementation',
  },
  'user.invited': {
    label: 'Someone was invited',
    title: (payload) => `${text(payload, 'invitedName') || text(payload, 'invitedEmail')} was invited`,
    body: (payload) => (text(payload, 'role') ? `Joining as ${text(payload, 'role')}` : ''),
    path: () => '/settings/organization-team',
  },
  'website.created': {
    label: 'Website added',
    title: (payload) => `${text(payload, 'websiteUrl')} was added`,
    path: () => '/websites',
  },
  'billing.mode_changed': {
    label: 'Billing mode changed',
    title: (payload) => `Billing switched to ${billingModeLabel(text(payload, 'newMode')) || 'a different mode'}`,
    body: (payload) => (text(payload, 'previousMode') ? `Was ${billingModeLabel(text(payload, 'previousMode'))}` : ''),
    path: () => '/settings/billing',
  },
  'billing.trial_extended': {
    label: 'Trial extended',
    title: () => 'Your trial was extended',
    body: (payload) => {
      const date = text(payload, 'newTrialEndsAt');
      // Spelled out in UTC: this text goes into emails rendered on the server,
      // so a bare toLocaleDateString() used the server's timezone and could
      // name the wrong day.
      return date
        ? `New trial end: ${new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })} (UTC)`
        : '';
    },
    path: () => '/settings/billing',
  },
  'billing.allowance_warning': {
    label: 'Session allowance 80% used',
    title: () => 'You have used 80% of your sessions this period',
    body: (payload) =>
      clientPrefix(payload) +
      `${count(payload, 'used')} of ${count(payload, 'included')} sessions on the ${planName(text(payload, 'plan'))} plan. ` +
      'Nothing stops if you go over: one month over is free.',
    path: () => '/settings/billing',
  },
  'billing.allowance_reached': {
    label: 'Session allowance reached',
    title: (payload) =>
      payload.capped
        ? "Your plan's sessions are used up: further sessions are ignored"
        : 'You have used all the sessions in your plan this period',
    body: (payload) =>
      clientPrefix(payload) +
      `${count(payload, 'used')} of ${count(payload, 'included')} sessions on the ${planName(text(payload, 'plan'))} plan. ` +
      (payload.capped
        ? `You chose to stay on this plan, so sessions over its limit are ignored until ${longDate(text(payload, 'windowEnd'))}. ` +
          'This applies to all of Pertento. Choose a larger plan to lift it now.'
        : 'Everything keeps running. A single month over is free; two months in a row moves you up a plan.'),
    path: () => '/settings/billing',
  },
  'billing.plan_upgrade_scheduled': {
    label: 'Plan upgrade scheduled',
    title: (payload) =>
      `You move up to ${planName(text(payload, 'nextPlan'))} on ${longDate(text(payload, 'appliesAt'))}`,
    body: (payload) =>
      clientPrefix(payload) +
      (text(payload, 'reason') === 'spike'
        ? `You used ${count(payload, 'used')} sessions this period, at least three times the ${count(payload, 'included')} ` +
          `in the ${planName(text(payload, 'plan'))} plan. `
        : `You have gone over your ${planName(text(payload, 'plan'))} sessions two months in a row. `) +
      'Your plan changes at the start of the next period. ' +
      `To stay on ${planName(text(payload, 'plan'))}, choose Keep my plan in Settings, Billing, before then.`,
    path: () => '/settings/billing',
  },
  'billing.plan_upgraded': {
    label: 'Plan upgraded',
    title: (payload) => `Your plan is moving up to ${planName(text(payload, 'nextPlan'))}`,
    body: (payload) =>
      clientPrefix(payload) +
      `As we told you, your usage was over your ${planName(text(payload, 'plan'))} sessions, so your plan is moving up. ` +
      'The price difference is charged now, prorated.',
    path: () => '/settings/billing',
  },
  'server_key.created': {
    label: 'Server key created',
    title: (payload) => `${serverKeyName(payload)} was created`,
    body: (payload) =>
      [serverKeyFacts(payload, 'Created'), 'If you did not expect this, revoke the key.'].filter(Boolean).join(' '),
    path: () => '/websites',
  },
  'server_key.revoked': {
    label: 'Server key revoked',
    title: (payload) => `${serverKeyName(payload)} was revoked`,
    body: (payload) =>
      [serverKeyFacts(payload, 'Revoked'), 'It stops working within about a minute.'].filter(Boolean).join(' '),
    path: () => '/websites',
  },
  'flag.published': {
    label: 'Flag published as scheduled',
    title: (payload) => `${flagName(payload)} was published as scheduled`,
    body: (payload) => text(payload, 'rollout') && `It is on for ${text(payload, 'rollout')} of visitors now.`,
    path: flagPath,
  },
  'flag.rollout_advanced': {
    label: 'Flag rollout moved to its next step',
    title: (payload) => `${flagName(payload)} is now on for ${text(payload, 'rollout') || 'more visitors'}`,
    body: (payload) => text(payload, 'step') && `Rollout plan ${text(payload, 'step')}.`,
    path: flagPath,
  },
  'flag.rollout_done': {
    label: 'Flag rollout finished',
    title: (payload) =>
      `${flagName(payload)} finished its rollout plan at ${text(payload, 'rollout') || 'its last step'}`,
    path: flagPath,
  },
  'flag.turned_off': {
    label: 'Flag turned off as scheduled',
    title: (payload) => `${flagName(payload)} was turned off as scheduled`,
    body: () => 'Nobody sees its On side now. Turn it back on from the flag page.',
    path: flagPath,
  },
  'flag.guardrail_breach': {
    label: 'Flag rollout paused by its guardrail',
    title: (payload) => `${flagName(payload)} was paused: On is doing worse than Off`,
    body: (payload) =>
      [
        text(payload, 'goal') && `On converts ${text(payload, 'relative') || 'less'} on ${text(payload, 'goal')}.`,
        `The rollout stays at ${text(payload, 'rollout') || 'its current share'}. Review it, then resume or turn it off.`,
      ]
        .filter(Boolean)
        .join(' '),
    path: flagPath,
  },
  'flag.implement_reminder': {
    label: 'Quick fix ready to build in',
    title: (payload) => `${flagName(payload)} has been on for everyone for 30 days`,
    body: () => 'Build the change into your site and archive the flag: changes applied in the browser cost page speed.',
    path: flagPath,
  },
};

export const FALLBACK_NOTIFICATION_TEXT: NotificationText = {
  label: 'Other activity',
  title: () => 'Something happened',
  path: () => null,
};

export const notificationText = (type: string): NotificationText =>
  NOTIFICATION_TEXT[type as NotificationTextType] ?? FALLBACK_NOTIFICATION_TEXT;

/** The verb an agent note opens with, by type; anything else an agent raised reads "Done". */
const AGENT_VERBS: Partial<Record<NotificationTextType, string>> = {
  'experiment.started': 'Started',
  'experiment.ended': 'Ended',
  'experiment.deployed': 'Deployed',
};

type AgentActionNoteParams = {
  type: string;
  payload: Record<string, unknown>;
  /**
   * Whether the person reading is the one the agent acts for: true for the token owner's own
   * panel row or email, false for everyone else, null where there is no single reader (Slack).
   */
  readerIsOwner: boolean | null;
};

/**
 * The line that marks an action an agent took (PER-348), from what createNotification put on the
 * payload when the request came with an agent token: "Deployed by your agent (Laptop)." for the
 * person the token belongs to, "Deployed by Jane Doe's agent." for anyone else. Empty
 * for a person's or Pertento's own action, so every surface can append it unconditionally.
 */
export function agentActionNote({ type, payload, readerIsOwner }: AgentActionNoteParams): string {
  if (payload.actorType !== 'agent') return '';
  const verb = AGENT_VERBS[type as NotificationTextType] ?? 'Done';
  // The token's name is a private label its owner chose ("Jane's laptop"), so only they see it.
  if (readerIsOwner) {
    const tokenName = text(payload, 'agentTokenName');
    return `${verb} by your agent${tokenName ? ` (${tokenName})` : ''}.`;
  }
  const actor = text(payload, 'actorName');
  return actor ? `${verb} by ${actor}'s agent.` : `${verb} by an agent.`;
}

type WithAgentNoteParams = AgentActionNoteParams & { body: string };

/** A body with the agent note after it, for the surfaces that show one line under the title. */
export function withAgentNote({ body, ...params }: WithAgentNoteParams): string {
  const note = agentActionNote(params);
  if (!note) return body;
  if (!body) return note;
  // Bodies are written without a closing full stop ("Rolled out B"), so one is added between.
  return `${/[.!?]$/.test(body) ? body : `${body}.`} ${note}`;
}
